import React, {createRef, ReactNode, RefObject} from "react";
import DomHelper from "../../../../shared/helper/DomHelper";
import MouseMode from "../../../viewer/settings/MouseMode";
import MouseFunction from "../../../viewer/settings/MouseFunction";
import {BorderStyle, Point, Rectangle} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import NumberHelper from "../../../../shared/helper/NumberHelper";
import {SVG_NAMESPACE} from "../../../../shared/annotation/Annotation";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";
import MenuButton from "../../../../shared/menu/MenuButton";
import Translate from "../../../../shared/translation/Translate";
import {I18nGlobals} from "../../../../shared/translation/i18nInterface";
import {ContextMenu} from "primereact/contextmenu";
import PortalComponent from "../../../../shared/components/PortalComponent";
import {MenuItem} from "primereact/menuitem";
import AnnotationEditBoxDialog from "../../../dialog/components/annotation/AnnotationEditBoxDialog";
import {DebugGlobals} from "../../../dialog/components/settings/DebugDialog";
import PortalContext from "../../../PortalContext";

interface LineSelectionProperties extends I18nGlobals, DebugGlobals {
	mouseMode?: MouseMode;
	mouseFunction?: MouseFunction;
	zoomValue?: number;
	startCallback?: (options: Array<Point> | null, event?: React.MouseEvent) => void;
	moveCallback?: (options: Array<Point> | null, event?: MouseEvent) => void;
	endCallback?: (options: Array<Point> | null, apply: boolean) => void;
	borderColor?: string;
	borderStyle?: BorderStyle;
	borderWidth?: number;
	opacity?: number;
	handleInteractivity?: () => void;
	confirmation?: boolean;
	getParent: () => HTMLElement | null;
	context: PortalContext;
}

interface LineSelectionState {
	lastPointerPosition: Point;
	points: Array<Point>;
	editIndex: number;
	active: boolean;
	edit: boolean;
	move: boolean;
	showBoxEditDialog: boolean;
}

export default class LineSelection extends PortalComponent<LineSelectionProperties, LineSelectionState> {
	private checking: boolean;
	private contextMenu: RefObject<ContextMenu>;

	constructor(props: Readonly<LineSelectionProperties>) {
		super(props);

		this.checking = false;
		this.state = {
			lastPointerPosition: Point.fromJson({
				x: 0,
				y: 0
			}),
			points: [
				Point.fromJson({
					x: 0,
					y: 0
				}),
				Point.fromJson({
					x: 0,
					y: 0
				})
			],
			editIndex: 0,
			active: false,
			edit: false,
			move: false,
			showBoxEditDialog: false
		};

		this.contextMenu = createRef<ContextMenu>();

		this.handlePointerDown = this.handlePointerDown.bind(this);
		this.handlePointerMove = this.handlePointerMove.bind(this);
		this.handlePointerUp = this.handlePointerUp.bind(this);
		this.handleMove = this.handleMove.bind(this);
		this.handleResize = this.handleResize.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.handleClick = this.handleClick.bind(this);
	}

	/**
	 * This is called when the component is mounted
	 */
	public async componentDidMount(): Promise<void> {
		await super.componentDidMount();

		let parent: HTMLElement | null = this.props.getParent();
		if (FunctionHelper.exists(parent)) {
			parent!.addEventListener("pointerdown", this.handlePointerDown);
		}

		// apply global events so events get applied outside the browser
		window.addEventListener("pointerup", this.handlePointerUp);
		window.addEventListener("pointermove", this.handlePointerMove);
		window.addEventListener("keydown", this.handleKeyDown);
	}

	/**
	 * This is called when the component is unmounted
	 */
	public async componentWillUnmount(): Promise<void> {
		await super.componentWillUnmount();

		let parent: HTMLElement | null = this.props.getParent();
		if (FunctionHelper.exists(parent)) {
			parent!.removeEventListener("pointerdown", this.handlePointerDown);
		}

		window.removeEventListener("pointerup", this.handlePointerUp);
		window.removeEventListener("pointermove", this.handlePointerMove);
		window.removeEventListener("keydown", this.handleKeyDown);
	}

	/**
	 * This is called when the components props or state is updated
	 */
	public async componentDidUpdate(
		prevProps: Readonly<LineSelectionProperties>, prevState: Readonly<LineSelectionState>, snapshot?: any
	): Promise<void> {
		let updates: any = {};

		if (this.props.mouseMode !== prevProps.mouseMode || this.props.mouseFunction !== prevProps.mouseFunction) {
			updates.active = false;
		}

		let zoomScale: number = FunctionHelper.applyValue(this.props.zoomValue, 1) / 100;
		let previousZoomScale: number = FunctionHelper.applyValue(prevProps.zoomValue, 1) / 100;

		if (zoomScale !== previousZoomScale) {
			let points: Array<Point> = this.state.points;

			for (let point of points) {
				point.x = (point.x! / previousZoomScale) * zoomScale
				point.y = (point.y! / previousZoomScale) * zoomScale
			}

			updates = {
				...updates,
				points: points
			} as LineSelectionState
		}

		if (Object.keys(updates).length > 0) {
			this.setState(updates);
		}
	}

	private async handleKeyDown(event: KeyboardEvent): Promise<void> {
		let parent: HTMLElement | null = this.props.getParent();

		if (!FunctionHelper.exists(parent) || !this.state.active || this.state.showBoxEditDialog) {
			return;
		}

		let points: Array<Point> = this.state.points;
		let containerRect: DOMRect = parent!.getBoundingClientRect();
		let mod: number = event.ctrlKey ? 5 : 1;

		switch (event.key.toLowerCase()) {
			case "arrowup":
				event.preventDefault();

				if (event.shiftKey) {
					if (points[1]!.y! - mod < 0) {
						points[1]!.y! = 0;
						return;
					}

					points[1]!.y! -= mod;
					break;
				}

				for (let point of points) {
					if (point.y! - mod <= 0) {
						return;
					}
				}

				for (let point of points) {
					point.y! -= mod;
				}
				break;
			case "arrowdown":
				event.preventDefault();

				if (event.shiftKey) {
					if (points[1]!.y! + mod > containerRect.height) {
						points[1]!.y! = containerRect.height;
						return;
					}

					points[1]!.y! += mod;
					break;
				}

				for (let point of points) {
					if (point.y! + mod >= containerRect.height) {
						return;
					}
				}

				for (let point of points) {
					point.y! += mod;
				}
				break;
			case "arrowleft":
				event.preventDefault();

				if (event.shiftKey) {
					if (points[1]!.x! - mod < 0) {
						points[1]!.x! = 0;
						return;
					}

					points[1]!.x! -= mod;
					break;
				}

				for (let point of points) {
					if (point.x! - mod <= 0) {
						return;
					}
				}

				for (let point of points) {
					point.x! -= mod;
				}
				break;
			case "arrowright":
				event.preventDefault();

				if (event.shiftKey) {
					if (points[1]!.x! + mod > containerRect.width) {
						points[1]!.x! = containerRect.width;
						return;
					}

					points[1]!.x! += mod;
					break;
				}

				for (let point of points) {
					if (point.x! + mod >= containerRect.width) {
						return;
					}
				}

				for (let point of points) {
					point.x! += mod;
				}
				break;
		}

		this.setState({
			points: points
		}, async () => {
			if (FunctionHelper.exists(this.props.endCallback)) {
				await this.props.endCallback!(this.state.points, true);
			}
		});
	}

	private async handlePointerUp(event: MouseEvent): Promise<void> {
		if (!this.state.active || !this.state.edit || event.button !== 0) {
			return;
		}

		event.stopPropagation();

		let points: Array<Point> = this.state.points;
		let isClick = false;
		let previous = Point.fromJson({
			x: -1,
			y: -1
		});

		for (let point of points) {
			if (point.x === previous.x && point.y === previous.y) {
				isClick = true;
				break;
			}

			previous.x = point.x;
			previous.y = point.y;
		}

		if (FunctionHelper.exists(this.props.endCallback) && !isClick) {
			this.props.endCallback!(this.state.points, !this.props.confirmation);
		}

		this.setState({
			edit: isClick,
			move: false
		});
	}

	private handlePointerDown(event: MouseEvent): void {
		let parent: HTMLElement | null = this.props.getParent();

		if (!FunctionHelper.exists(parent) || event.button !== 0 ||
			!DomHelper.hasClass(event.target as HTMLElement, parent!.className)) {
			return;
		}

		event.stopPropagation();

		if (FunctionHelper.exists(this.props.handleInteractivity)) {
			this.props.handleInteractivity!();
		}

		let containerRect: DOMRect = parent!.getBoundingClientRect();
		let xPos: number = event.clientX - containerRect.x;
		let yPos: number = event.clientY - containerRect.y;

		let points: Array<Point> = this.state.points;
		for (let point of points) {
			point.x = xPos;
			point.y = yPos;
		}

		this.setState({
			lastPointerPosition: Point.fromJson({
				x: xPos,
				y: yPos
			}),
			points: points,
			active: true,
			edit: true,
			editIndex: points.length - 1
		}, () => {
			if (FunctionHelper.exists(this.props.startCallback)) {
				this.props.startCallback!(this.state.points);
			}
		});
	}

	private handlePointerMove(event: MouseEvent): void {
		let parent: HTMLElement | null = this.props.getParent();

		if (!FunctionHelper.exists(parent) || !this.state.active || !this.state.edit || this.checking) {
			return;
		}

		event.stopPropagation();

		this.checking = true;
		let points: Array<Point> = this.state.points;
		let containerRect: DOMRect = parent!.getBoundingClientRect();
		let currentPosition: Point = Point.fromJson({
			x: NumberHelper.limit(event.clientX - containerRect.x, 0, containerRect.width),
			y: NumberHelper.limit(event.clientY - containerRect.y, 0, containerRect.height)
		});

		let deltaX: number = currentPosition.x! - this.state.lastPointerPosition.x!;
		let deltaY: number = currentPosition.y! - this.state.lastPointerPosition.y!;
		let editIndex: number = this.state.editIndex;

		if (this.state.move) {
			// check if any of the points would be out of bounds
			for (let point of points) {
				if (point.x! + deltaX < 0) {
					deltaX = -point.x!;
					currentPosition.x = this.state.lastPointerPosition.x!;
				}

				if (point.x! + deltaX > containerRect.width) {
					deltaX = containerRect.width - point.x!;
					currentPosition.x = this.state.lastPointerPosition.x!;
				}

				if (point.y! + deltaY < 0) {
					deltaY = -point.y!;
					currentPosition.y = this.state.lastPointerPosition.y!;
				}

				if (point.y! + deltaY > containerRect.height) {
					deltaY = containerRect.height - point.y!;
					currentPosition.y = this.state.lastPointerPosition.y!;
				}
			}

			for (let point of points) {
				point.x! = point.x! + deltaX;
				point.y! = point.y! + deltaY;
			}
		} else {
			points[editIndex] = Point.fromJson({
				x: NumberHelper.limit(points[editIndex].x! + deltaX, 0, containerRect.width),
				y: NumberHelper.limit(points[editIndex].y! + deltaY, 0, containerRect.height)
			});
		}

		this.setState({
			points: points,
			lastPointerPosition: currentPosition
		}, async () => {
			if (FunctionHelper.exists(this.props.moveCallback)) {
				await this.props.moveCallback!(this.state.points, event);
			}

			this.checking = false;
		});
	}

	private handleMove(event: React.MouseEvent): void {
		let parent: HTMLElement | null = this.props.getParent();

		if (!FunctionHelper.exists(parent) || event.button !== 0) {
			return;
		}

		event.stopPropagation();

		let containerRect: DOMRect = parent!.getBoundingClientRect();

		this.setState({
			move: true,
			edit: true,
			lastPointerPosition: Point.fromJson({
				x: event.clientX - containerRect.x,
				y: event.clientY - containerRect.y
			})
		});
	}


	private handleResize(index: number, event: React.MouseEvent): void {
		if (event.button !== 0) {
			return;
		}

		event.stopPropagation();
		let points: Array<Point> = this.state.points;

		this.setState({
			edit: true,
			editIndex: index,
			lastPointerPosition: Point.fromJson({
				x: points[index].x!,
				y: points[index].y!
			})
		});
	}

	private handleClick(e: React.MouseEvent): void {
		if (e.button === 2 && FunctionHelper.exists(this.contextMenu.current)) {
			e.preventDefault();
			e.stopPropagation();
			this.contextMenu.current!.show(e);
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		let self: LineSelection = this;
		let points: Array<Point> = this.state.points;
		let resizer: Array<ReactNode> = [];

		let lineWidth: number = 0;
		let lineHeight: number = 0;
		let lineXPos: number = 0;
		let lineYPos: number = 0;

		for (let point of points.values()) {
			if ((lineXPos === 0 && point.x! > 0) || point.x! < lineXPos) {
				lineXPos = point.x!;
			}

			if ((lineYPos === 0 && point.y! > 0) || point.y! < lineYPos) {
				lineYPos = point.y!;
			}

			if (lineWidth === 0 && point.x! > 0) {
				lineWidth = point.x!;
			} else if (point.x! < lineWidth) {
				lineWidth = lineWidth - point.x!;
			} else {
				lineWidth = point.x! - lineWidth;
			}

			if (lineHeight === 0 && point.y! > 0) {
				lineHeight = point.y!;
			} else if (point.y! < lineHeight) {
				lineHeight = lineHeight - point.y!;
			} else {
				lineHeight = point.y! - lineHeight;
			}
		}

		for (let [index, point] of points.entries()) {
			resizer.push(
				<div className="resize" key={index}
					 style={{
						 left: (point.x! - lineXPos),
						 top: (point.y! - lineYPos)
					 }}/>
			);

			resizer.push(
				<div className="resize-area" key={index + "area"}
					 style={{
						 left: (point.x! - lineXPos),
						 top: (point.y! - lineYPos)
					 }}
					 onPointerDown={(event: React.MouseEvent) => this.handleResize(index, event)}/>
			);
		}

		let borderColor: string = FunctionHelper.applyValue(this.props.borderColor, "#000");
		let borderStyle: BorderStyle = FunctionHelper.applyValue(this.props.borderStyle, BorderStyle.Solid);
		let borderWidth: number = FunctionHelper.applyValue(this.props.borderWidth, 1);
		let opacity: number = FunctionHelper.applyValue(this.props.opacity, 100) / 100;

		let selectionClasses: Array<string> = ["line-selection"];

		if (this.state.active) {
			selectionClasses.push("active");
		}

		let contextMenu: Array<MenuItem> = [
			{
				label: Translate.value("buttons.cancel"),
				icon: 'icon icon-close',
				command(e) {
					if (FunctionHelper.exists(self.props.endCallback)) {
						self.props.endCallback!(null, false);
					}

					self.setState({
						active: false
					});
				}
			}
		];

		if (this.props.confirmation) {
			contextMenu = [{
				label: Translate.value("buttons.confirm"),
				icon: 'icon icon-check',
				command(e) {
					if (FunctionHelper.exists(self.props.endCallback)) {
						self.props.endCallback!(self.state.points, true);
					}
				}
			}, ...contextMenu];
		}

		contextMenu = [{
			label: Translate.value("buttons.edit"),
			icon: 'icon icon-resize',
			command(e) {
				self.setState({
					showBoxEditDialog: true
				});
			}
		}, ...contextMenu];

		return (
			<>
				<div className={selectionClasses.join(" ")} style={{
					width: lineWidth,
					height: lineHeight,
					left: lineXPos,
					top: lineYPos
				}}>
					<div className={"popup" + (!this.state.edit ? " active" : "")}>
						<div className="popup-content">
							<MenuButton {...this.props} icon="icon icon-resize"
										tooltip={Translate.value("buttons.edit")}
										onClick={() => this.setState({
											showBoxEditDialog: true
										})}
							/>
							{this.props.confirmation &&
                                <MenuButton {...this.props} icon="icon icon-check"
                                            tooltip={Translate.value("buttons.confirm")}
                                            onClick={() => {
												if (FunctionHelper.exists(this.props.endCallback)) {
													this.props.endCallback!(this.state.points, true);
												}
											}}
                                />
							}
							<MenuButton {...this.props} icon="icon icon-close"
										tooltip={Translate.value("buttons.cancel")}
										onClick={() => {
											if (FunctionHelper.exists(this.props.endCallback)) {
												this.props.endCallback!(null, false);
											}

											this.setState({
												active: false
											})
										}}
							/>
						</div>
					</div>
					<svg xmlns={SVG_NAMESPACE} onClick={this.handleClick} onContextMenu={this.handleClick}
						 style={{
							 opacity: opacity
						 }}>
						<polyline className="movearea" onPointerDown={this.handleMove}
								  points={this.state.points.map(
									  point => (point.x! - lineXPos) + "," + (point.y! - lineYPos)
								  ).join(" ")}
								  style={{
									  strokeWidth: borderWidth + 20
								  }}/>
						<polyline className="preview"
								  points={this.state.points.map(
									  point => (point.x! - lineXPos) + "," + (point.y! - lineYPos)
								  ).join(" ")}
								  style={{
									  strokeDasharray: borderStyle === BorderStyle.Solid ? "none" : 5,
									  strokeWidth: borderWidth,
									  stroke: borderColor
								  }}/>
					</svg>
					{resizer}
				</div>
				<ContextMenu appendTo={document.getElementById("root")} model={contextMenu}
							 ref={this.contextMenu}/>
				<AnnotationEditBoxDialog {...this.props} showHelp={false} parameter={this.state.points}
										 getParent={this.props.getParent} zoomValue={this.props.zoomValue}
										 visible={this.state.showBoxEditDialog} fixedHeight={true} fixedWidth={true}
										 hide={() => this.setState({
											 showBoxEditDialog: false
										 })}
										 submit={(result: Point | Rectangle | Array<Point>) => {
											 if (result instanceof Array) {
												 this.setState({
													 points: result.map((point: Point) => point.clone())
												 }, async () => {
													 if (FunctionHelper.exists(this.props.endCallback)) {
														 await this.props.endCallback!(this.state.points, true);
													 }
												 });
											 }
										 }}/>
			</>
		);
	}
}